import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import { CardSkeleton } from "@/components/Skeleton";
import {
  CalendarCog, CalendarOff, Check, Clock, Loader2, Plus, Stethoscope, Trash2, X,
} from "lucide-react";

/** Settings → Scheduling: the clinic's booking rules, its visit types, and who works when.
 *
 *  F1 shipped these endpoints with no UI, so the only way to change how the clinic books
 *  was to PUT JSON by hand. Everything here is owner-editable and read-only for everyone
 *  else, except a provider's own hours and time off — the backend already lets a provider
 *  manage those, and making them ask an owner to move their Thursday is how a schedule
 *  stops matching reality.
 */

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const GRANULARITIES = [5, 10, 15, 20, 30, 60];

/** Minutes that `tz` is offset from UTC at a given instant. */
function tzOffsetMinutes(date, tz) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(date).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day,
                         parts.hour === "24" ? 0 : +parts.hour, +parts.minute, +parts.second);
  return (asUtc - date.getTime()) / 60000;
}

/** "YYYY-MM-DDTHH:MM" typed as CLINIC-local wall time → the UTC instant it names.
 *
 *  Not the browser's timezone: an owner travelling, or a virtual front desk in another
 *  state, would otherwise block out the wrong hours. Two passes because the offset
 *  itself depends on the instant, which is what a DST boundary turns on.
 */
function clinicLocalToUtcIso(local, tz) {
  const naive = Date.parse(`${local}:00Z`);
  if (Number.isNaN(naive)) return null;
  let ts = naive;
  for (let i = 0; i < 2; i++) ts = naive - tzOffsetMinutes(new Date(ts), tz) * 60000;
  return new Date(ts).toISOString().replace(".000Z", "+00:00");
}

function utcToClinicLabel(isoUtc, tz) {
  return new Date(isoUtc).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

function Card({ icon: Icon, title, description, children, testid, action }) {
  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">{title}</h3>
          </div>
          {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Num({ label, hint, value, onChange, disabled, testid, min, max }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</span>
      <input type="number" min={min} max={max} data-testid={testid} disabled={disabled} value={value}
             onChange={(e) => onChange(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
             className="sinput mt-1.5" />
      {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
    </label>
  );
}

function Toggle({ label, hint, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" data-testid={testid} disabled={disabled} checked={!!checked}
             onChange={(e) => onChange(e.target.checked)}
             className="w-4 h-4 rounded border-input mt-0.5" />
      <span>
        <span className="text-sm">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

// --- Booking rules ---------------------------------------------------------
function BookingRules({ canEdit }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/clinic/scheduling")
      .then((r) => setForm(r.data))
      .catch((e) => { setForm({}); setError(formatApiError(e.response?.data?.detail)); });
  }, []);

  if (!form) return <CardSkeleton lines={4} />;

  const set = (k) => (v) => { setForm({ ...form, [k]: v }); setSaved(false); };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put("/clinic/scheduling", {
        booking_enabled: form.booking_enabled,
        min_lead_time_hours: form.min_lead_time_hours,
        max_advance_days: form.max_advance_days,
        cancel_cutoff_hours: form.cancel_cutoff_hours,
        reschedule_cutoff_hours: form.reschedule_cutoff_hours,
        reminder_offsets_hours: form.reminder_offsets_hours,
        reminder_include_visit_type: form.reminder_include_visit_type,
        waitlist_enabled: form.waitlist_enabled,
        waitlist_offer_window_min: form.waitlist_offer_window_min,
        allow_double_booking_override: form.allow_double_booking_override,
      });
      setForm(data); setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Card icon={CalendarCog} title="Booking rules"
          description="How far ahead patients can book, and how late they can change their minds."
          testid="scheduling-config">
      {!canEdit && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
          Only the Clinic Owner can change booking rules. Viewing in read-only mode.
        </p>
      )}

      <div className="space-y-4">
        <Toggle testid="sched-booking-enabled" disabled={!canEdit}
                label="Let patients book online"
                hint="Turning this off leaves staff booking untouched — patients are asked to message the clinic instead."
                checked={form.booking_enabled} onChange={set("booking_enabled")} />

        <div className="grid sm:grid-cols-2 gap-4">
          <Num testid="sched-min-lead" disabled={!canEdit} label="Minimum notice (hours)" min={0} max={720}
               hint="Patients can't grab a slot closer than this."
               value={form.min_lead_time_hours} onChange={set("min_lead_time_hours")} />
          <Num testid="sched-max-advance" disabled={!canEdit} label="Book up to (days ahead)" min={1} max={730}
               value={form.max_advance_days} onChange={set("max_advance_days")} />
          <Num testid="sched-cancel-cutoff" disabled={!canEdit} label="Cancellation cutoff (hours)" min={0} max={720}
               hint="Inside this window the portal hides Cancel and points them at messaging."
               value={form.cancel_cutoff_hours} onChange={set("cancel_cutoff_hours")} />
          <Num testid="sched-reschedule-cutoff" disabled={!canEdit} label="Reschedule cutoff (hours)" min={0} max={720}
               value={form.reschedule_cutoff_hours} onChange={set("reschedule_cutoff_hours")} />
        </div>

        <div className="pt-3 border-t border-border space-y-3">
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Reminders</p>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">
              Send reminders this many hours before
            </span>
            <input data-testid="sched-reminder-offsets" disabled={!canEdit}
                   value={(form.reminder_offsets_hours || []).join(", ")}
                   onChange={(e) => {
                     const offs = e.target.value.split(",").map((s) => parseInt(s.trim(), 10))
                       .filter((n) => Number.isInteger(n));
                     set("reminder_offsets_hours")(offs);
                   }}
                   placeholder="24, 2" className="sinput mt-1.5" />
            <span className="block text-xs text-muted-foreground mt-1">Comma-separated whole hours.</span>
          </label>
          <Toggle testid="sched-reminder-visit-type" disabled={!canEdit}
                  label="Name the visit type in reminders"
                  hint="Off by default, deliberately: “Injection Visit” on a lock screen discloses care to whoever is looking at the phone."
                  checked={form.reminder_include_visit_type} onChange={set("reminder_include_visit_type")} />
        </div>

        <div className="pt-3 border-t border-border space-y-3">
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Waitlist & overrides</p>
          <Toggle testid="sched-waitlist-enabled" disabled={!canEdit} label="Offer a waitlist when nothing is open"
                  checked={form.waitlist_enabled} onChange={set("waitlist_enabled")} />
          <Num testid="sched-waitlist-window" disabled={!canEdit} label="Waitlist offer expires after (minutes)"
               min={5} max={10080} value={form.waitlist_offer_window_min}
               onChange={set("waitlist_offer_window_min")} />
          <Toggle testid="sched-allow-override" disabled={!canEdit}
                  label="Let staff book over a conflict"
                  hint="For squeeze-ins. Always reason-prompted and recorded in the audit log."
                  checked={form.allow_double_booking_override} onChange={set("allow_double_booking_override")} />
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="save-scheduling-button"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
            {saved ? "Saved" : "Save booking rules"}
          </button>
        )}
      </div>
    </Card>
  );
}

// --- Visit types -----------------------------------------------------------
const BLANK_VT = {
  name: "", duration_min: 30, booking_mode: "instant", patient_bookable: true,
  buffer_after_min: 0, provider_ids: [], location_ids: [], color: "#1E293B",
  is_virtual: false, active: true,
};

function VisitTypeRow({ vt, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(vt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.put(`/visit-types/${vt.id}`, {
        name: form.name, duration_min: form.duration_min, booking_mode: form.booking_mode,
        patient_bookable: form.patient_bookable, buffer_after_min: form.buffer_after_min,
        provider_ids: form.provider_ids || [], location_ids: form.location_ids || [],
        color: form.color, is_virtual: form.is_virtual, active: form.active,
      });
      setEditing(false); onSaved();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 py-3"
           data-testid={`visit-type-row-${vt.id}`}>
        <div className="min-w-0">
          <p className="text-sm font-medium flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: vt.color || "#1E293B" }} />
            {vt.name}
            {!vt.active && <span className="text-xs text-muted-foreground">· retired</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {vt.duration_min} min
            {vt.buffer_after_min ? ` + ${vt.buffer_after_min} min turnover` : ""}
            {" · "}
            {vt.booking_mode === "request" ? "patient requests, staff confirm" : "books instantly"}
            {vt.patient_bookable ? "" : " · staff only"}
            {vt.is_virtual ? " · virtual" : ""}
          </p>
        </div>
        {canEdit && (
          <button data-testid={`visit-type-edit-${vt.id}`} onClick={() => { setForm(vt); setEditing(true); }}
                  className="shrink-0 text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary">
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="py-4 space-y-3" data-testid={`visit-type-form-${vt.id}`}>
      <VisitTypeFields form={form} setForm={setForm} idPrefix={vt.id} />
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={busy} data-testid={`visit-type-save-${vt.id}`}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
        <button onClick={() => { setEditing(false); setError(""); }}
                className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
      </div>
    </div>
  );
}

function VisitTypeFields({ form, setForm, idPrefix }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="block sm:col-span-2">
        <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Name</span>
        <input data-testid={`vt-name-${idPrefix}`} value={form.name}
               onChange={(e) => set("name", e.target.value)} className="sinput mt-1.5"
               placeholder="e.g. Injection Visit" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Duration (min)</span>
        <input type="number" min="5" max="480" data-testid={`vt-duration-${idPrefix}`} value={form.duration_min}
               onChange={(e) => set("duration_min", parseInt(e.target.value, 10) || 0)} className="sinput mt-1.5" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Turnover after (min)</span>
        <input type="number" min="0" max="240" data-testid={`vt-buffer-${idPrefix}`} value={form.buffer_after_min}
               onChange={(e) => set("buffer_after_min", parseInt(e.target.value, 10) || 0)} className="sinput mt-1.5" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Booking</span>
        <select data-testid={`vt-mode-${idPrefix}`} value={form.booking_mode}
                onChange={(e) => set("booking_mode", e.target.value)} className="sinput mt-1.5">
          <option value="instant">Books instantly</option>
          <option value="request">Patient requests, staff confirm</option>
        </select>
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Colour</span>
        <input type="color" data-testid={`vt-color-${idPrefix}`} value={form.color || "#1E293B"}
               onChange={(e) => set("color", e.target.value)}
               className="mt-1.5 h-10 w-16 rounded-md border border-border cursor-pointer" />
      </label>
      <div className="sm:col-span-2 space-y-2 pt-1">
        <Toggle testid={`vt-bookable-${idPrefix}`} label="Patients can book this themselves"
                checked={form.patient_bookable} onChange={(v) => set("patient_bookable", v)} />
        <Toggle testid={`vt-virtual-${idPrefix}`} label="Virtual visit (creates a telehealth link)"
                checked={form.is_virtual} onChange={(v) => set("is_virtual", v)} />
        <Toggle testid={`vt-active-${idPrefix}`} label="Active"
                hint="Retiring a visit type hides it from booking. Past appointments keep referring to it, so it is never deleted."
                checked={form.active} onChange={(v) => set("active", v)} />
      </div>
    </div>
  );
}

function VisitTypes({ canEdit }) {
  const [types, setTypes] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_VT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/visit-types", { params: { include_inactive: true } })
      .then((r) => setTypes(r.data))
      .catch((e) => { setTypes([]); setError(formatApiError(e.response?.data?.detail)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/visit-types", form);
      setAdding(false); setForm(BLANK_VT); load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Card icon={Stethoscope} title="Visit types"
          description="What can be booked, how long it takes, and whether it confirms itself."
          testid="visit-type-editor"
          action={canEdit && !adding && (
            <button data-testid="visit-type-add-button" onClick={() => { setForm(BLANK_VT); setAdding(true); }}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md px-3 py-2 hover:bg-primary/90">
              <Plus className="w-4 h-4" /> Add
            </button>
          )}>
      {adding && (
        <div className="border border-border rounded-lg p-4 mb-4 space-y-3" data-testid="visit-type-new-form">
          <VisitTypeFields form={form} setForm={setForm} idPrefix="new" />
          <div className="flex items-center gap-2">
            <button onClick={create} disabled={busy || !form.name.trim()} data-testid="visit-type-create-button"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create visit type
            </button>
            <button onClick={() => { setAdding(false); setError(""); }}
                    className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">{error}</p>}

      {types === null ? <CardSkeleton lines={3} />
        : types.length === 0 ? <p className="text-sm text-muted-foreground">No visit types yet.</p>
        : (
          <div className="divide-y divide-border" data-testid="visit-type-list">
            {types.map((vt) => (
              <VisitTypeRow key={vt.id} vt={vt} canEdit={canEdit} onSaved={load} />
            ))}
          </div>
        )}
    </Card>
  );
}

// --- Provider hours + time off ---------------------------------------------
function ProviderHours({ isAdmin, selfId }) {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";

  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState(null);   // null = still loading, [] = none exist
  const [providerId, setProviderId] = useState("");
  const [rows, setRows] = useState(null);          // [{location_id, weekday, blocks, slot_granularity_min}]
  const [timeOff, setTimeOff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [off, setOff] = useState({ start: "", end: "", reason: "" });

  const canEdit = isAdmin || providerId === selfId;

  useEffect(() => {
    api.get("/staff").then((r) => {
      setStaff(r.data);
      setProviderId((cur) => cur || r.data.find((s) => s.role === "provider")?.id || r.data[0]?.id || "");
    }).catch(() => setStaff([]));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
  }, []);

  const load = useCallback(() => {
    if (!providerId) return;
    setRows(null);
    api.get(`/providers/${providerId}/schedule`)
      .then((r) => { setRows(r.data.schedules || []); setTimeOff(r.data.time_off || []); })
      .catch((e) => { setRows([]); setError(formatApiError(e.response?.data?.detail)); });
  }, [providerId]);
  useEffect(() => { load(); }, [load]);

  // One editable row per location per weekday. Built from whatever the provider already
  // has, then filled in — so a clinic with two sites gets two grids without anything here
  // assuming there is exactly one.
  const grid = useMemo(() => {
    if (rows === null || locations === null) return null;
    return locations.map((loc) => ({
      location: loc,
      days: WEEKDAYS.map((_, wd) => {
        const existing = rows.find((r) => r.location_id === loc.id && r.weekday === wd);
        return existing
          ? { ...existing, blocks: existing.blocks || [] }
          : { location_id: loc.id, weekday: wd, blocks: [], slot_granularity_min: 15 };
      }),
    }));
  }, [rows, locations]);

  const mutate = (locId, weekday, patch) => {
    setSaved(false);
    setRows((cur) => {
      const list = (cur || []).slice();
      const i = list.findIndex((r) => r.location_id === locId && r.weekday === weekday);
      const base = i >= 0 ? list[i] : { location_id: locId, weekday, blocks: [], slot_granularity_min: 15 };
      const next = { ...base, ...patch };
      if (i >= 0) list[i] = next; else list.push(next);
      return list;
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      // Wholesale replace, matching the endpoint. Days with no blocks simply aren't sent —
      // that is how a weekday gets cleared.
      const payload = (rows || [])
        .filter((r) => (r.blocks || []).length > 0)
        .map((r) => ({
          location_id: r.location_id, weekday: r.weekday, blocks: r.blocks,
          slot_granularity_min: r.slot_granularity_min || 15,
          effective_from: r.effective_from || null, effective_to: r.effective_to || null,
        }));
      await api.put(`/providers/${providerId}/schedule`, payload);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const addTimeOff = async () => {
    setError("");
    const start = clinicLocalToUtcIso(off.start, tz);
    const end = clinicLocalToUtcIso(off.end, tz);
    if (!start || !end) { setError("Give a start and end for the time off."); return; }
    try {
      await api.post(`/providers/${providerId}/time-off`, {
        start_utc: start, end_utc: end, reason: off.reason, all_day: false,
      });
      setOff({ start: "", end: "", reason: "" });
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const removeTimeOff = async (id) => {
    try {
      await api.delete(`/providers/${providerId}/time-off/${id}`);
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card icon={Clock} title="Working hours"
          description="Weekly hours drive every open slot the availability engine offers."
          testid="provider-hours-editor"
          action={
            <select data-testid="provider-hours-select" value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    className="shrink-0 text-sm border border-border rounded-md px-3 py-2 bg-card">
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          }>
      {!canEdit && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
          You can view these hours. Owners can edit anyone's; providers can edit their own.
        </p>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3"
                   data-testid="provider-hours-error">{error}</p>}

      {grid === null ? <CardSkeleton lines={4} />
        : grid.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No locations configured yet, so there is nowhere to attach hours to.
          </p>
        ) : grid.map(({ location, days }) => (
          <div key={location.id} className="mb-5" data-testid={`hours-location-${location.id}`}>
            {/* Named only when there is more than one — a single-site clinic doesn't need
                to be told which building it is in. */}
            {locations.length > 1 && (
              <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground mb-2">
                {location.name}
              </p>
            )}
            <div className="divide-y divide-border border border-border rounded-lg">
              {days.map((row) => (
                <div key={row.weekday} className="p-3 flex flex-wrap items-center gap-3"
                     data-testid={`hours-row-${location.id}-${row.weekday}`}>
                  <span className="text-sm font-medium w-24 shrink-0">{WEEKDAYS[row.weekday]}</span>

                  <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    {row.blocks.length === 0 && (
                      <span className="text-xs text-muted-foreground">Not working</span>
                    )}
                    {row.blocks.map((b, i) => (
                      <span key={i} className="inline-flex items-center gap-1">
                        <input type="time" step="300" disabled={!canEdit} value={b.start}
                               data-testid={`hours-start-${location.id}-${row.weekday}-${i}`}
                               onChange={(e) => mutate(location.id, row.weekday, {
                                 blocks: row.blocks.map((x, j) => j === i ? { ...x, start: e.target.value } : x),
                               })}
                               className="border border-input bg-card rounded-md px-2 py-1 text-sm" />
                        <span className="text-xs text-muted-foreground">to</span>
                        <input type="time" step="300" disabled={!canEdit} value={b.end}
                               data-testid={`hours-end-${location.id}-${row.weekday}-${i}`}
                               onChange={(e) => mutate(location.id, row.weekday, {
                                 blocks: row.blocks.map((x, j) => j === i ? { ...x, end: e.target.value } : x),
                               })}
                               className="border border-input bg-card rounded-md px-2 py-1 text-sm" />
                        {canEdit && (
                          <button data-testid={`hours-remove-${location.id}-${row.weekday}-${i}`}
                                  onClick={() => mutate(location.id, row.weekday, {
                                    blocks: row.blocks.filter((_, j) => j !== i),
                                  })}
                                  className="text-muted-foreground hover:text-destructive">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    ))}
                    {canEdit && (
                      <button data-testid={`hours-add-${location.id}-${row.weekday}`}
                              onClick={() => mutate(location.id, row.weekday, {
                                blocks: [...row.blocks, { start: "09:00", end: "17:00" }],
                              })}
                              className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1 hover:bg-secondary">
                        <Plus className="w-3 h-3" /> {row.blocks.length ? "Split" : "Add hours"}
                      </button>
                    )}
                  </div>

                  {row.blocks.length > 0 && (
                    <select disabled={!canEdit} value={row.slot_granularity_min || 15}
                            data-testid={`hours-granularity-${location.id}-${row.weekday}`}
                            onChange={(e) => mutate(location.id, row.weekday,
                                                    { slot_granularity_min: parseInt(e.target.value, 10) })}
                            className="text-xs border border-border rounded-md px-2 py-1 bg-card shrink-0">
                      {GRANULARITIES.map((g) => <option key={g} value={g}>{g}-min slots</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

      {canEdit && grid?.length > 0 && (
        <button onClick={save} disabled={saving} data-testid="save-hours-button"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
          {saved ? "Saved" : "Save working hours"}
        </button>
      )}

      {/* Time off */}
      <div className="mt-6 pt-5 border-t border-border" data-testid="time-off-editor">
        <div className="flex items-center gap-2 mb-1">
          <CalendarOff className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-heading text-base font-semibold">Time off</h4>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Subtracted from availability. Times are the clinic's local time.
        </p>

        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <label className="block">
              <span className="text-xs text-muted-foreground">From</span>
              <input type="datetime-local" data-testid="time-off-start" value={off.start}
                     onChange={(e) => setOff({ ...off, start: e.target.value })}
                     className="block border border-input bg-card rounded-md px-2 py-1.5 text-sm mt-1" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">To</span>
              <input type="datetime-local" data-testid="time-off-end" value={off.end}
                     onChange={(e) => setOff({ ...off, end: e.target.value })}
                     className="block border border-input bg-card rounded-md px-2 py-1.5 text-sm mt-1" />
            </label>
            <input data-testid="time-off-reason" value={off.reason} placeholder="Reason (optional)"
                   onChange={(e) => setOff({ ...off, reason: e.target.value })}
                   className="border border-input bg-card rounded-md px-3 py-1.5 text-sm" />
            <button onClick={addTimeOff} disabled={!off.start || !off.end} data-testid="time-off-add-button"
                    className="bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60">
              Add
            </button>
          </div>
        )}

        {timeOff.length === 0 ? (
          <p className="text-sm text-muted-foreground">No time off booked.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="time-off-list">
            {timeOff.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 py-2.5"
                   data-testid={`time-off-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm">
                    {utcToClinicLabel(t.start_utc, tz)} → {utcToClinicLabel(t.end_utc, tz)}
                  </p>
                  {t.reason && <p className="text-xs text-muted-foreground">{t.reason}</p>}
                </div>
                {canEdit && (
                  <button onClick={() => removeTimeOff(t.id)} data-testid={`time-off-remove-${t.id}`}
                          className="shrink-0 text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function SchedulingSettings() {
  const { user } = useAuth();
  const isAdmin = ["owner", "system_admin"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="settings-scheduling">
      <style>{`.sinput{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.sinput:focus{box-shadow:0 0 0 2px hsl(var(--ring))}.sinput:disabled{background:hsl(var(--secondary));color:hsl(var(--muted-foreground))}`}</style>
      <BookingRules canEdit={isAdmin} />
      <VisitTypes canEdit={isAdmin} />
      <ProviderHours isAdmin={isAdmin} selfId={user?.id} />
    </div>
  );
}
